import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import AppBreadcrumbs from "@/components/AppBreadcrumbs";
import { toast } from "sonner";
import {
  Play,
  Pause,
  Copy,
  Sparkle,
  ArrowClockwise,
  ClipboardText,
  ChatCircle,
  Brain,
  Target,
  MagnifyingGlass,
  ChartBar,
  Warning,
  Heart,
  Lightbulb,
  Question,
  Flag,
  TrendUp,
  ClockCounterClockwise,
  WarningCircle,
  CheckCircle,
  Trash,
  X,
  Info,
  UserCircle,
  Megaphone,
  Barbell,
  ArrowCircleUpRight,
  ChatsCircle,
  Handshake,
  CaretDown,
  ArrowLeft,
  ArrowRight,
  PhoneCall,
} from "@phosphor-icons/react";

const STYLES = [
  { key: "first_person", label: "First-Person" },
  { key: "salesforce", label: "Salesforce" },
  { key: "hubspot", label: "HubSpot" },
  { key: "close", label: "Close" },
  { key: "minimal", label: "Minimal" },
  { key: "executive", label: "Executive" },
  { key: "detailed_history", label: "Detailed History" },
];

function fmt(t) {
  if (t == null) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function fmtDateTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function transcriptSpeakerTone(speaker) {
  const normalized = String(speaker || "").trim().toLowerCase();

  if (["rep", "sales rep", "sales representative", "agent"].includes(normalized)) {
    return "is-rep";
  }
  if (["client", "customer", "prospect"].includes(normalized)) {
    return "is-client";
  }
  if (/^(?:speaker\s*)?a$/.test(normalized)) {
    return "is-a";
  }
  if (/^(?:speaker\s*)?b$/.test(normalized)) {
    return "is-b";
  }
  return "";
}

// Normalize a confidence value into 'high' | 'medium' | 'low'
function confLevel(c) {
  if (c == null) return null;
  if (typeof c === "number") {
    if (c >= 75) return "high";
    if (c >= 50) return "medium";
    return "low";
  }
  const s = String(c).toLowerCase();
  if (s === "high" || s === "medium" || s === "low") return s;
  return null;
}

function ConfChip({ confidence, testId }) {
  const lvl = confLevel(confidence);
  if (!lvl) return null;

  return (
    <span
      data-testid={testId}
      className={`confidence-chip confidence-chip--${lvl}`}
    >
      {lvl}
    </span>
  );
}

// Extract text + confidence from either a plain string or a {text, confidence} object
function itemParts(it) {
  if (it == null) return { text: "", confidence: null };
  if (typeof it === "string") {
    return { text: localizeSellerRole(it), confidence: null };
  }
  if (typeof it === "object") {
    return {
      text: localizeSellerRole(it.text || it.description || JSON.stringify(it)),
      confidence: it.confidence,
    };
  }
  return { text: localizeSellerRole(String(it)), confidence: null };
}

function localizeSellerRole(value) {
  if (typeof value !== "string") return value;

  const normalized = value
    .replace(/\bDer Commercial\b/g, "Der Vertriebsmitarbeiter")
    .replace(/\bder Commercial\b/g, "der Vertriebsmitarbeiter")
    .replace(/\bDen Commercial\b/g, "Den Vertriebsmitarbeiter")
    .replace(/\bden Commercial\b/g, "den Vertriebsmitarbeiter")
    .replace(/\bDem Commercial\b/g, "Dem Vertriebsmitarbeiter")
    .replace(/\bdem Commercial\b/g, "dem Vertriebsmitarbeiter")
    .replace(/\bEin Commercial\b/g, "Ein Vertriebsmitarbeiter")
    .replace(/\bein Commercial\b/g, "ein Vertriebsmitarbeiter")
    .replace(/\bEinen Commercial\b/g, "Einen Vertriebsmitarbeiter")
    .replace(/\beinen Commercial\b/g, "einen Vertriebsmitarbeiter");

  if (!/sales[ _-]?rep/i.test(normalized)) return normalized;
  const lower = normalized.toLocaleLowerCase();
  let role = "sales rep";
  if (/\b(?:il|lo|la|cliente|impegno|impegnat|verificare|aggiornare|possibilit[aà]|pomeriggio)\b/i.test(lower)) {
    role = "commerciale";
  } else if (/\b(?:le|la|client|engagement|vérifier|mettre à jour|commerciale?)\b/i.test(lower)) {
    role = "commercial";
  } else if (/\b(?:el|la|cliente|compromiso|verificar|actualizar|comercial)\b/i.test(lower)) {
    role = "representante comercial";
  } else if (/\b(?:der|die|kunde|zusage|prüfen|aktualisieren|vertrieb)\b/i.test(lower)) {
    role = "Vertriebsmitarbeiter";
  }
  return normalized.replace(/sales[ _-]?rep/gi, role);
}

function longitudinalItemText(item) {
  if (item == null) return "";
  if (typeof item === "string") return localizeSellerRole(item);
  if (typeof item !== "object") return String(item);
  return localizeSellerRole(item.what || item.text || item.description || item.answer || "");
}

function comparisonTokens(value) {
  const stopWords = new Set([
    "alla", "allo", "anche", "come", "dalla", "delle", "della", "degli", "dello", "dopo",
    "into", "from", "that", "the", "this", "with", "pour", "dans", "avec", "para", "con",
  ]);
  return new Set(
    longitudinalItemText(value)
      .toLocaleLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .match(/[a-z0-9]+/g)
      ?.filter((token) => token.length >= 4 && !stopWords.has(token)) || []
  );
}

function substantiallyOverlaps(left, right) {
  const a = comparisonTokens(left);
  const b = comparisonTokens(right);
  if (!a.size || !b.size) return false;
  let shared = 0;
  a.forEach((token) => {
    if (b.has(token)) shared += 1;
  });
  return shared / Math.min(a.size, b.size) >= 0.6;
}

function redundantHeadline(item, objections, themes, promises) {
  const text = longitudinalItemText(item).toLocaleLowerCase();
  const promiseCue = /(?:impegn|promess|commit|sales[ _-]?rep|commerciale|commercial|zusage)/i;
  const objectionCue = /(?:obiezion|objection|objeción|einwand)/i;
  const themeCue = /(?:\btema\b|\btheme\b|\bthème\b|\bthema\b)/i;
  if (promises.length && promiseCue.test(text)) return true;
  if (objections.length && objectionCue.test(text)) return true;
  if (themes.length && themeCue.test(text)) return true;
  return [...objections, ...themes, ...promises].some((detail) => substantiallyOverlaps(item, detail));
}

function signalValue(value) {
  if (value == null || value === "") return "Not established";
  if (typeof value === "object") {
    return value.text ?? value.score ?? value.value ?? "Not established";
  }
  return String(value);
}

function DeltaList({ title, items, tone = "violet" }) {
  const visible = Array.isArray(items) ? items.filter(longitudinalItemText).slice(0, 4) : [];
  if (!visible.length) return null;
  const colors = {
    violet: "text-violet-300",
    amber: "text-amber-300",
    emerald: "text-emerald-300",
    red: "text-red-300",
  };
  return (
    <div className="longitudinal-list">
      <div className={`longitudinal-list__title ${colors[tone] || colors.violet}`}>
        {title}
      </div>
      <ul className="longitudinal-list__items">
        {visible.map((item, index) => (
          <li key={index} className="flex gap-2">
            <span className={colors[tone] || colors.violet}>•</span>
            <span>{longitudinalItemText(item)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LongitudinalDelta({ update, relationshipPath, relationshipName, relationshipReturnState }) {
  const [expanded, setExpanded] = useState(true);
  if (!update) return null;

  const changes = update.changes || {};
  const isComparison = Boolean(update.previous_call_id);
  const signalChanges = changes.signal_changes || {};
  const signalDefinitions = [
    ["Stance", signalChanges.attitude],
    ["Buying intent", signalChanges.buying_intent],
    ["Trust", signalChanges.trust],
    ["Tone", signalChanges.emotional_tone],
  ];
  const visibleSignals = isComparison
    ? signalDefinitions.filter(([, signal]) => signal && (signal.from != null || signal.to != null))
    : [];
  const friction = changes.friction || {};
  const commercial = friction.commercial_objections || changes.objections || {};
  const blockers = friction.operational_blockers || {};
  const preferences = friction.process_preferences || {};
  const commitments = changes.commitments || {};
  const legacyPromises = changes.promises || {};
  const themes = changes.themes || changes.recurring_themes || {};

  const candidates = [
    ...(Array.isArray(changes.summary) ? changes.summary : []),
    ...(commercial.new || []),
    ...(commercial.recurring || []),
    ...(blockers.new || []),
    ...(blockers.recurring || []),
    ...(preferences.new || []),
    ...(commitments.new || legacyPromises.made || []),
    ...(commitments.completed || legacyPromises.fulfilled || []),
    ...(themes.new || []),
    ...(themes.recurring || []),
  ];

  const visibleItems = candidates
    .filter(longitudinalItemText)
    .filter((item, index, all) => (
      !all.slice(0, index).some((previous) => substantiallyOverlaps(item, previous))
    ))
    .slice(0, 4);

  if (!visibleItems.length) return null;

  const title = isComparison ? "Relationship update" : "Initial relationship context";

  return (
    <section
      className={`longitudinal-delta longitudinal-delta--compact${expanded ? " is-expanded" : " is-collapsed"}`}
      data-testid="longitudinal-call-delta"
    >
      <div className="longitudinal-delta__header">
        <button
          type="button"
          className="longitudinal-delta__toggle"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          aria-controls="relationship-context-details"
        >
          <span className="longitudinal-delta__title">
            <ClockCounterClockwise size={17} weight="duotone" />
            {title}
          </span>
        </button>

        <div className="longitudinal-delta__header-actions">
          {relationshipPath && (
            <Link
              to={relationshipPath}
              state={relationshipReturnState}
              className="longitudinal-delta__relationship-link"
              aria-label={`Open live relationship context for ${relationshipName || "this customer"}`}
            >
              <span>Live relationship context</span>
              <ArrowRight size={14} weight="bold" aria-hidden="true" />
            </Link>
          )}
          <button
            type="button"
            className="longitudinal-delta__expand-button"
            onClick={() => setExpanded((current) => !current)}
            aria-expanded={expanded}
            aria-controls="relationship-context-details"
            aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`}
          >
            <CaretDown
              size={17}
              weight="bold"
              className="longitudinal-delta__chevron"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {expanded && (
        <div id="relationship-context-details" className="longitudinal-delta__content">
          <ul className="longitudinal-delta__summary longitudinal-delta__summary--compact">
            {visibleItems.map((item, index) => (
              <li key={index}>
                <TrendUp size={15} aria-hidden="true" />
                <span>{longitudinalItemText(item)}</span>
              </li>
            ))}
          </ul>

          {visibleSignals.length > 0 && (
            <div className="longitudinal-signal-grid" aria-label="Updated relationship signals">
              {visibleSignals.map(([label, signal]) => (
                <div key={label} className="longitudinal-signal">
                  <div className="longitudinal-signal__label">{label}</div>
                  <div className="longitudinal-signal__value">
                    <span className="text-slate-500">{signalValue(signal.from)}</span>
                    <span className="mx-2 text-violet-400">→</span>
                    <span>{signalValue(signal.to)}</span>
                  </div>
                  {signal.direction && signal.direction !== "unknown" && (
                    <div className="longitudinal-signal__direction">{signal.direction}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const SCORE_LABELS = {
  human_writing: "Human writing",
  template_language: "Template language",
  business_reasoning: "Business reasoning",
  negotiation_insight: "Negotiation insight",
  emotional_accuracy: "Emotional accuracy",
  customer_psychology: "Customer psychology",
  future_usefulness: "Future usefulness",
  writing_dna_similarity: "Writing DNA match",
};

const SCORE_DESCRIPTIONS = {
  human_writing: "How natural and human the note sounds, without formulaic or AI-like phrasing.",
  template_language: "How well the note avoids generic, repetitive, or canned CRM language.",
  business_reasoning: "How clearly the note explains the commercial logic, decisions, and cause-and-effect behind the conversation.",
  negotiation_insight: "How well the note captures objections, leverage, trade-offs, and decision dynamics.",
  emotional_accuracy: "How faithfully the note reflects tone, reactions, tension, rapport, and conversational subtext.",
  customer_psychology: "How clearly the note captures the customer's motivations, concerns, intent, and behavior.",
  future_usefulness: "How useful the note will be when preparing the next contact and deciding what to do next.",
  writing_dna_similarity: "How closely the note matches the user's learned writing style and reasoning patterns.",
};

function scoreTextClass(value, invert = false) {
  return `score-text score-text--${scoreTone(value, invert)}`;
}

function QualityScorePanel({ scores }) {
  if (!scores) return null;
  const cats = Object.keys(SCORE_LABELS);
  const overall = scores.overall || 0;
  const dnaProfile = scores.dna_profile || {};
  const dnaAlignment = scores.dna_alignment || {};
  const weakCategories = cats.filter((key) => Number.isFinite(scores[key]?.score) && scores[key].score < 70);

  return (
    <section className="note-quality" data-testid="quality-scores">
      <div className="note-quality__header">
        <div>
          <div className="note-quality__title">Note quality</div>
          <p>Scores always refer to the CRM note version currently shown above.</p>
          {dnaAlignment.applied && (
            <span className="note-quality__alignment">Writing DNA alignment applied · {dnaAlignment.before_match} → {dnaAlignment.after_match}</span>
          )}
        </div>
        <div className="note-quality__overall">
          <span className={scoreTextClass(overall)} data-testid="overall-score">{overall}</span>
          <small>overall</small>
        </div>
      </div>

      <div className="note-quality__grid">
        {cats.map((key, index) => {
          const value = scores[key] || {};
          const isDna = key === "writing_dna_similarity";
          const calibrated = isDna
            ? Boolean(dnaProfile.calibrated && Number.isFinite(value.score))
            : Number.isFinite(value.score);
          const score = calibrated ? Math.max(0, Math.min(100, value.score)) : 0;
          return (
            <div
              key={key}
              className={`note-quality__metric ${calibrated ? `note-quality__metric--${scoreTone(score)}` : "note-quality__metric--uncalibrated"}`}
              data-testid={`score-${key}`}
              style={{ animationDelay: `${index * 45}ms` }}
            >
              <div className="note-quality__metric-head">
                <div className="note-quality__metric-label">
                  <span>{SCORE_LABELS[key]}</span>
                  <MetricHelp label={SCORE_LABELS[key]} description={SCORE_DESCRIPTIONS[key]} />
                </div>
                <strong className={calibrated ? scoreTextClass(score) : "score-text--muted"}>
                  {calibrated ? value.score : "—"}
                </strong>
              </div>
              <div className={`score-meter${calibrated ? "" : " is-uncalibrated"}`}>
                <div
                  className={calibrated ? `score-meter__fill score-meter__fill--${scoreTone(score)}` : "score-meter__fill"}
                  style={{ width: `${calibrated ? score : 0}%` }}
                />
              </div>
              {isDna && (
                <div className="note-quality__metric-meta">
                  {dnaProfile.calibrated ? (
                    <>
                      <span>{dnaProfile.maturity_label || "Calibrated profile"}</span>
                      <span>{dnaProfile.confidence}% profile confidence</span>
                    </>
                  ) : (
                    <span>Not calibrated · add CRM examples in Writing DNA</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {weakCategories.length > 0 && (
        <div className="note-quality__weak">
          <div className="note-quality__weak-title">Areas to strengthen</div>
          {weakCategories.map((key) => {
            const value = scores[key];
            return (
              <article key={key} data-testid={`weak-${key}`} className={`note-quality__weak-card note-quality__weak-card--${scoreTone(value.score)}`}>
                <div className="note-quality__weak-head">
                  <span>{SCORE_LABELS[key]}</span>
                  <strong className={scoreTextClass(value.score)}>{value.score}%</strong>
                </div>
                {value.reason && (
                  <p><span>Reason</span>{value.reason}</p>
                )}
                {value.suggestion && (
                  <p><span className="text-emerald-400">Suggested improvement</span>{value.suggestion}</p>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

const INTEL_QS = [
  ["real_business_problem", "Real business problem"],
  ["actual_concern", "Actual concern"],
  ["stated_objection", "Stated objection"],
  ["unstated_objection", "Hidden objection"],
  ["what_changed", "What changed"],
  ["turning_point", "Turning point"],
  ["six_month_memory", "Remember in 6 months"],
  ["churn_reason", "Churn risk"],
  ["renewal_reason", "Renewal driver"],
];

const INTEL_META = {
  real_business_problem: {
      label: "Business problem",
      icon: <Lightbulb size={15} weight="fill" />,
      color: "text-violet-400",
  },

  actual_concern: {
      label: "Actual concern",
      icon: <Question size={15} weight="fill" />,
      color: "text-violet-400",
  },

  stated_objection: {
      label: "Stated objection",
      icon: <Flag size={15} weight="fill" />,
      color: "text-emerald-400",
  },

  unstated_objection: {
      label: "Hidden objection",
      icon: <WarningCircle size={15} weight="fill" />,
      color: "text-violet-400",
  },

  what_changed: {
      label: "Progress made",
      icon: <TrendUp size={15} weight="fill" />,
      color: "text-emerald-400",
  },

  turning_point: {
      label: "Turning point",
      icon: <CheckCircle size={15} weight="fill" />,
      color: "text-emerald-400",
  },

  six_month_memory: {
      label: "Remember this",
      icon: <ClockCounterClockwise size={15} weight="fill" />,
      color: "text-cyan-400",
  },

  churn_reason: {
      label: "Churn risk",
      icon: <Warning size={15} weight="fill" />,
      color: "text-amber-400",
  },

  renewal_reason: {
      label: "Renewal driver",
      icon: <Heart size={15} weight="fill" />,
      color: "text-emerald-400",
  },
};

function RevealLinesMark({ compact = false }) {
  return (
    <span className={`reveal-lines-mark${compact ? " reveal-lines-mark--compact" : ""}`} aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
    </span>
  );
}

function IntelligencePanel({ callId, intelligence, onLoaded }) {
  const [loading, setLoading] = useState(false);
  const [findingsStep, setFindingsStep] = useState(0);
  const [displayItems, setDisplayItems] = useState(() => intelligence?.items || {});
  const has = Object.keys(displayItems).length > 0;

  const run = async () => {
    setLoading(true);
    setFindingsStep(1);
    const timer = setTimeout(() => setFindingsStep(2), 1200);

    try {
      const { data } = await api.post(`/calls/${callId}/intelligence`);
      onLoaded(data);
      setDisplayItems({});

      const newItems = data?.items || {};
      let revealed = 0;
      const revealTimer = setInterval(() => {
        const [key] = INTEL_QS[revealed] || [];
        if (key && newItems[key]) {
          setDisplayItems((current) => ({ ...current, [key]: newItems[key] }));
        }
        revealed += 1;
        if (revealed >= INTEL_QS.length) clearInterval(revealTimer);
      }, 180);
    } catch {
      toast.error("Findings generation failed");
    } finally {
      clearTimeout(timer);
      setLoading(false);
      setFindingsStep(0);
    }
  };

  return (
    <aside className="findings-panel findings-panel--note-companion" data-testid="intelligence-panel">
      <div className="findings-panel__header">
        <div className="findings-panel__identity">
          <RevealLinesMark compact />
          <div className="findings-panel__title">Findings</div>
        </div>
        <button
          onClick={run}
          disabled={loading}
          data-testid="intel-refresh"
          className="call-detail-secondary-button findings-panel__action"
        >
          <ArrowClockwise size={12} weight="bold" />
          {loading ? "Working" : has ? "Regenerate" : "Generate"}
        </button>
      </div>

      <div className="segmented-divider" />

      {loading ? (
        <div className="findings-processing" role="status" aria-live="polite">
          {["Understanding the conversation", "Finding what was left unsaid"].map((label, index) => {
            const step = index + 1;
            const complete = findingsStep > step;
            const active = findingsStep === step;
            return (
              <div key={label} className={`findings-processing__step${active ? " is-active" : ""}${complete ? " is-complete" : ""}`}>
                <span className="findings-processing__number">{complete ? "✓" : step}</span>
                <span>{label}</span>
              </div>
            );
          })}
          <RevealLinesMark />
        </div>
      ) : !has ? (
        <div className="findings-panel__empty">
          <RevealLinesMark />
          <strong>Evidence for a stronger note.</strong>
          <p>Surface what was explicit, implied, changing, or still unresolved.</p>
        </div>
      ) : (
        <div className="findings-stack intel-scrollbar">
          {INTEL_QS.map(([key, label], index) => {
            const value = displayItems[key] || {};
            if (!displayItems[key]) return null;

            const meta = INTEL_META[key];
            const isFact = value.type === "fact";
            const confidence = value.confidence ?? 0;
            const tone = key === "churn_reason" || key.includes("objection")
              ? "risk"
              : key === "renewal_reason" || key === "what_changed" || key === "turning_point"
                ? "positive"
                : key === "six_month_memory"
                  ? "cyan"
                  : "violet";

            return (
              <article
                key={key}
                className={`finding-card finding-card--${tone}`}
                data-testid={`intel-${key}`}
                style={{ animationDelay: `${index * 65}ms` }}
              >
                <div className="finding-card__accent" />
                <div className="finding-card__header">
                  <div className="finding-card__label">
                    <span className={meta?.color || "text-violet-400"}>{meta?.icon}</span>
                    <strong>{meta?.label || label}</strong>
                  </div>
                  <span className="finding-card__index">{String(index + 1).padStart(2, "0")}</span>
                </div>

                <div className="finding-card__badges">
                  <span className={isFact ? "is-fact" : "is-inference"}>{isFact ? "fact" : "inference"}</span>
                  <ConfChip confidence={confidence} testId={`intel-conf-${key}`} />
                  <span className="finding-card__percentage">{confidence}%</span>
                </div>

                <p className="finding-card__answer">{localizeSellerRole(value.answer)}</p>
                {value.evidence && value.evidence !== "no direct evidence" && (
                  <blockquote className="finding-card__evidence">“{localizeSellerRole(value.evidence)}”</blockquote>
                )}
              </article>
            );
          })}
        </div>
      )}
    </aside>
  );
}

export default function CallDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relationshipContext, setRelationshipContext] = useState(null);
  const requestedTab = searchParams.get("section");
  const deepDiveTarget = searchParams.get("deepDiveTarget") || location.state?.deepDiveTarget || null;
  const activeTab = ["notes", "transcript", "analysis", "coaching"].includes(requestedTab)
    ? requestedTab
    : "analysis";
  const setActiveTab = useCallback((key) => {
    const next = new URLSearchParams(searchParams);
    if (key === "analysis") next.delete("section");
    else next.set("section", key);
    setSearchParams(next);
  }, [searchParams, setSearchParams]);
  const [activeStyle, setActiveStyle] = useState("first_person");
  const [crmNoteLanguage, setCrmNoteLanguage] = useState(
    () => localStorage.getItem("callsect_crm_note_language") || "english"
  );
  const [generating, setGenerating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [findingsStep, setFindingsStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [transcriptTargetIndex, setTranscriptTargetIndex] = useState(null);
  const [highlightedTranscriptIndex, setHighlightedTranscriptIndex] = useState(null);
  const audioRef = useRef();
  const deepDiveTargetHandledRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/calls/${id}`);
    setCall(data);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    setRelationshipContext(null);
    window.scrollTo({ top: 0, left: 0 });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const normalize = (value) => String(value || "").trim().toLocaleLowerCase();

    const loadRelationshipContext = async () => {
      if (!call?.id || (!call.customer_name && !call.relationship_id)) {
        if (!cancelled) setRelationshipContext(null);
        return;
      }

      try {
        const { data: customers } = await api.get("/customers");
        const directKey = call.relationship_id || call.relationship_key;
        const normalizedName = normalize(call.customer_name);
        const normalizedCompany = normalize(call.company);

        const matchedCustomer = (customers || []).find((customer) => (
          directKey
          && [customer.key, customer.id].some((value) => String(value || "") === String(directKey))
        )) || (customers || []).find((customer) => (
          normalize(customer.name) === normalizedName
          && (!normalizedCompany || !customer.company || normalize(customer.company) === normalizedCompany)
        )) || (directKey ? {
          key: directKey,
          id: directKey,
          name: call.customer_name,
          company: call.company,
        } : null);

        const relationshipKey = matchedCustomer?.key || matchedCustomer?.id;
        if (!relationshipKey) {
          if (!cancelled) setRelationshipContext(null);
          return;
        }

        const { data: timeline } = await api.get(
          `/customers/${encodeURIComponent(relationshipKey)}/timeline`
        );
        const chronologicalCalls = [...(timeline?.milestones || [])].sort(
          (left, right) => new Date(left.date).getTime() - new Date(right.date).getTime()
        );
        const currentIndex = chronologicalCalls.findIndex(
          (milestone) => String(milestone.call_id) === String(call.id)
        );

        if (!cancelled) {
          setRelationshipContext({
            key: relationshipKey,
            name: timeline?.customer?.name || matchedCustomer?.name || call.customer_name,
            company: timeline?.customer?.company || matchedCustomer?.company || call.company,
            previousCall: currentIndex > 0 ? chronologicalCalls[currentIndex - 1] : null,
            nextCall: currentIndex >= 0 && currentIndex < chronologicalCalls.length - 1
              ? chronologicalCalls[currentIndex + 1]
              : null,
          });
        }
      } catch {
        if (!cancelled) setRelationshipContext(null);
      }
    };

    loadRelationshipContext();
    return () => {
      cancelled = true;
    };
  }, [call?.company, call?.customer_name, call?.id, call?.relationship_id, call?.relationship_key]);

  useEffect(() => {
    localStorage.setItem("callsect_crm_note_language", crmNoteLanguage);
  }, [crmNoteLanguage]);

  const openTranscriptAt = (seconds) => {
    const segments = Array.isArray(call?.segments) ? call.segments : [];
    if (!segments.length || !Number.isFinite(seconds)) {
      setActiveTab("transcript");
      return;
    }

    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    segments.forEach((segment, index) => {
      const start = Number(segment?.start);
      if (!Number.isFinite(start)) return;
      const distance = Math.abs(start - seconds);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    setTranscriptTargetIndex(nearestIndex);
    setActiveTab("transcript");
  };

  useEffect(() => {
    if (activeTab !== "transcript" || transcriptTargetIndex == null) return undefined;

    const frame = window.requestAnimationFrame(() => {
      const target = document.querySelector(
        `[data-transcript-index="${transcriptTargetIndex}"]`
      );
      if (!target) return;

      target.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedTranscriptIndex(transcriptTargetIndex);
    });

    const clearHighlight = window.setTimeout(() => {
      setHighlightedTranscriptIndex(null);
      setTranscriptTargetIndex(null);
    }, 2600);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(clearHighlight);
    };
  }, [activeTab, transcriptTargetIndex]);

  useEffect(() => {
    const targetKey = deepDiveTarget;
    if (!targetKey || !call?.analysis) return undefined;

    const token = `${id}:${targetKey}`;
    if (deepDiveTargetHandledRef.current === token) return undefined;

    if (activeTab !== "analysis") {
      setActiveTab("analysis");
      return undefined;
    }

    let clearTarget = null;
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(`deep-dive-${targetKey}`)
        || document.getElementById("deep-dive");
      if (!target) return;

      target.scrollIntoView({ behavior: "smooth", block: "center" });
      target.classList.add("is-linked-target");
      deepDiveTargetHandledRef.current = token;
      clearTarget = window.setTimeout(() => {
        target.classList.remove("is-linked-target");
      }, 2600);
    });

    return () => {
      window.cancelAnimationFrame(frame);
      if (clearTarget) window.clearTimeout(clearTarget);
    };
  }, [activeTab, call?.analysis, deepDiveTarget, id, setActiveTab]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    setFindingsStep(1);
    const findingsTimer = setTimeout(() => {
      setFindingsStep(2);
    }, 1500);
    try {
      const { data } = await api.post(`/calls/${id}/analyze`);
      setCall((c) => ({
        ...c,
        analysis: data.analysis,
        coaching: data.coaching,
        relationship_update: data.relationship_update || c.relationship_update,
      }));
      toast.success("Analysis complete");
    } catch {
      toast.error("Analysis failed");
    } finally {
      clearTimeout(findingsTimer);
      setAnalyzing(false);
      setFindingsStep(0);
    }
  };

  const generate = async (style) => {
    setGenerating(true);
    setActiveStyle(style);
    try {
      const { data } = await api.post(`/calls/${id}/notes/generate`, {
        style,
        use_writing_profile: true,
        output_language: crmNoteLanguage,
      });
      setCall((c) => ({
        ...c,
        notes: {
          ...(c.notes || {}),
          [style]: {
            content: data.content,
            scores: data.scores,
            language_mode: data.language_mode,
            dna_alignment: data.dna_alignment || null,
          },
        },
      }));
      toast.success(`${style.replace(/_/g, " ")} note ready`);
    } catch {
      toast.error("Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const rewrite = async (instruction) => {
    const current = call?.notes?.[activeStyle]?.content;
    if (!current) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/calls/${id}/notes/rewrite`, {
        text: current,
        instruction,
        style: activeStyle,
      });
      setCall((c) => ({
        ...c,
        notes: {
          ...(c.notes || {}),
          [activeStyle]: {
            ...(c.notes?.[activeStyle] || {}),
            content: data.content,
            scores: data.scores ?? null,
            dna_alignment: data.dna_alignment || null,
          },
        },
      }));
      toast.success(data.scores ? "Rewritten and rescored" : "Rewritten");
    } catch {
      toast.error("Rewrite failed");
    } finally {
      setGenerating(false);
    }
  };

  const copy = () => {
    const c = call?.notes?.[activeStyle]?.content;
    if (!c) return;
    navigator.clipboard.writeText(c);
    toast.success("Copied to clipboard");
  };

  const download = (format) => {
    const c = call?.notes?.[activeStyle]?.content;
    if (!c) return;
    const text = format === "md" ? `# ${call.title}\n\n${c}` : c;
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${call.title || "note"}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteCall = async () => {
    setDeleting(true);
    try {
      await api.delete(`/calls/${id}`);
      toast.success("Call deleted");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      const message = error?.response?.data?.detail || "The call could not be deleted";
      toast.error(message);
      setDeleting(false);
    }
  };

  if (loading) return (
    <div className="call-detail-page">
      <Nav />
      <main className="call-detail-shell">
        <div className="call-detail-loading" role="status" aria-live="polite">
          <span />
          <span />
          <span />
          <span className="sr-only">Loading call...</span>
        </div>
      </main>
    </div>
  );
  if (!call) return (
    <div className="call-detail-page">
      <Nav />
      <main className="call-detail-shell">
        <div className="call-detail-not-found">
          <PhoneCall size={28} weight="duotone" />
          <strong>Call not found.</strong>
          <button type="button" onClick={() => navigate("/dashboard")} className="call-detail-secondary-button">Back to calls</button>
        </div>
      </main>
    </div>
  );

  const currentNote = call.notes?.[activeStyle]?.content;
  const findingsReady = Boolean(Object.keys(call.intelligence_panel?.items || {}).length);
  const noteEnrichmentState = findingsReady
    ? currentNote
      ? {
          tone: "ready",
          title: "Findings ready",
          message: "Regenerate to let Callsect use these additional insights.",
        }
      : {
          tone: "ready",
          title: "Findings ready",
          message: "These insights will be used in the first draft.",
        }
    : currentNote
      ? {
          tone: "pending",
          title: "Want a more informed version?",
          message: "Generate Findings, then regenerate the note.",
        }
      : {
          tone: "pending",
          title: "Get a richer first draft",
          message: "Generate Findings first to let Callsect use additional evidence and context.",
        };
  const analysis = call.analysis;
  const coaching = call.coaching;
  const relationshipKey = relationshipContext?.key || location.state?.relationshipKey;
  const relationshipName = relationshipContext?.name || location.state?.relationshipName || call.customer_name;
  const relationshipPath = relationshipKey
    ? `/customers/${encodeURIComponent(relationshipKey)}`
    : null;
  const cameFromRelationship = location.state?.from === "relationship" && relationshipPath;
  const backTarget = cameFromRelationship ? relationshipPath : "/";
  const backLabel = cameFromRelationship ? "Back to relationship" : "Back to calls";
  const relationshipReturnState = relationshipPath
    ? { activeCallId: call.id }
    : undefined;

  return (
    <div className="call-detail-page">
      <Nav />
      <main className="call-detail-shell">
        <div className="call-detail-context-topbar">
          <AppBreadcrumbs
            items={[
              ...(relationshipPath ? [{ label: "Customers", to: "/customers" }] : []),
              ...(relationshipPath && relationshipName
                ? [{ label: relationshipName, to: relationshipPath, state: relationshipReturnState }]
                : []),
              { label: call.title || "Call" },
            ]}
          />

          <Link
            to={backTarget}
            state={relationshipReturnState}
            className="call-detail-back-link"
          >
            <ArrowLeft size={15} weight="bold" />
            {backLabel}
          </Link>
        </div>

        <header className="call-detail-header">
          <div className="call-detail-header__identity">
            <span className="call-detail-header__mark" aria-hidden="true">
              <PhoneCall size={24} weight="duotone" />
            </span>
            <div className="call-detail-header__copy">
              <div className="call-detail-header__eyebrow">Conversation intelligence</div>
              <h1>{call.title}</h1>
              <div className="call-detail-meta">
                {call.customer_name && (
                  relationshipPath ? (
                    <Link
                      to={relationshipPath}
                      state={relationshipReturnState}
                      className="call-detail-meta__relationship"
                    >
                      {call.customer_name}
                    </Link>
                  ) : (
                    <span>{call.customer_name}</span>
                  )
                )}
                {call.company && <span>{call.company}</span>}
                <span>{fmtDateTime(call.occurred_at || call.created_at)}</span>
                <span>{fmt(call.duration)}</span>
              </div>
            </div>
          </div>

          <div className="call-detail-actions">
            {!analysis && (
              <button
                onClick={runAnalysis}
                disabled={analyzing}
                data-testid="run-analysis-btn"
                className="call-detail-primary-button"
              >
                <Target size={16} weight="fill" /> {analyzing ? "Analyzing..." : "Analyze call"}
              </button>
            )}

            <button
              type="button"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="open-delete-call-dialog"
              className="call-detail-danger-button call-detail-danger-button--header"
            >
              <Trash size={16} weight="fill" />
              Delete call
            </button>
          </div>
        </header>

        {relationshipPath && (
          <nav className="call-context-navigation" aria-label="Conversation navigation">
            <div className="call-context-navigation__sequence">
              {relationshipContext?.previousCall ? (
                <Link
                  to={`/calls/${relationshipContext.previousCall.call_id}`}
                  state={{
                    from: "relationship",
                    relationshipKey,
                    relationshipName,
                  }}
                  className="call-context-navigation__call"
                  aria-label={`Open previous call: ${relationshipContext.previousCall.title || "Previous call"}`}
                >
                  <ArrowLeft size={19} weight="bold" aria-hidden="true" />
                  <span>
                    <small>Previous call</small>
                    <strong>{relationshipContext.previousCall.title || "Earlier conversation"}</strong>
                    {relationshipContext.previousCall.date && (
                      <em>{fmtDateTime(relationshipContext.previousCall.date)}</em>
                    )}
                  </span>
                </Link>
              ) : (
                <span className="call-context-navigation__call is-disabled">
                  <ArrowLeft size={19} weight="bold" aria-hidden="true" />
                  <span>
                    <small>Previous call</small>
                    <strong>No earlier conversation</strong>
                  </span>
                </span>
              )}

              {relationshipContext?.nextCall ? (
                <Link
                  to={`/calls/${relationshipContext.nextCall.call_id}`}
                  state={{
                    from: "relationship",
                    relationshipKey,
                    relationshipName,
                  }}
                  className="call-context-navigation__call is-next"
                  aria-label={`Open next call: ${relationshipContext.nextCall.title || "Next call"}`}
                >
                  <span>
                    <small>Next call</small>
                    <strong>{relationshipContext.nextCall.title || "Later conversation"}</strong>
                    {relationshipContext.nextCall.date && (
                      <em>{fmtDateTime(relationshipContext.nextCall.date)}</em>
                    )}
                  </span>
                  <ArrowRight size={19} weight="bold" aria-hidden="true" />
                </Link>
              ) : (
                <span className="call-context-navigation__call is-next is-disabled">
                  <span>
                    <small>Next call</small>
                    <strong>No later conversation</strong>
                  </span>
                  <ArrowRight size={19} weight="bold" aria-hidden="true" />
                </span>
              )}
            </div>
          </nav>
        )}

        {deleteDialogOpen && (
          <div className="call-detail-dialog-backdrop" role="dialog" aria-modal="true" aria-labelledby="delete-call-title" data-testid="delete-call-dialog">
            <div className="call-detail-dialog">
              <div className="call-detail-dialog__header">
                <div>
                  <div className="call-detail-dialog__danger"><Trash size={20} weight="fill" /></div>
                  <h2 id="delete-call-title">Delete this call?</h2>
                </div>
                <button type="button" onClick={() => setDeleteDialogOpen(false)} disabled={deleting} aria-label="Close" className="call-detail-dialog__close">
                  <X size={18} />
                </button>
              </div>
              <p><strong>{call.title}</strong>{call.customer_name ? ` for ${call.customer_name}` : ""} will be permanently removed, including its transcript, analysis, coaching and CRM notes.</p>
              <small>This action cannot be undone.</small>
              <div className="call-detail-dialog__actions">
                <button type="button" onClick={() => setDeleteDialogOpen(false)} disabled={deleting} className="call-detail-secondary-button">Cancel</button>
                <button type="button" onClick={deleteCall} disabled={deleting} data-testid="confirm-delete-call" className="call-detail-danger-button">
                  <Trash size={15} weight="fill" /> {deleting ? "Deleting..." : "Delete call"}
                </button>
              </div>
            </div>
          </div>
        )}

        <LongitudinalDelta
          update={call.relationship_update}
          relationshipPath={relationshipPath}
          relationshipName={relationshipName}
          relationshipReturnState={relationshipReturnState}
        />

        <nav className="call-detail-tabs" aria-label="Call detail sections" role="tablist">
          {[
            { key: "notes", label: "CRM Notes", icon: ClipboardText },
            { key: "transcript", label: "Transcript", icon: ChatCircle },
            { key: "analysis", label: "Dissection", icon: MagnifyingGlass },
            { key: "coaching", label: "Coaching", icon: ChartBar },
          ].map((tab) => (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`call-detail-tab${activeTab === tab.key ? " is-active" : ""}`}
              role="tab"
              id={`call-tab-${tab.key}`}
              aria-controls={`call-panel-${tab.key}`}
              aria-selected={activeTab === tab.key}
              tabIndex={activeTab === tab.key ? 0 : -1}
            >
              <tab.icon size={16} weight={activeTab === tab.key ? "fill" : "regular"} />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div
          className="call-detail-tab-content"
          key={activeTab}
          role="tabpanel"
          id={`call-panel-${activeTab}`}
          aria-labelledby={`call-tab-${activeTab}`}
        >
          {activeTab === "notes" && (
            <section>
              <div className="call-detail-section-intro">
                <div>
                  <div className="brand-eyebrow">Your voice, structured</div>
                  <h2>Generate notes in your own writing style.</h2>
                  <p>Choose a format, select the output language, then refine the note without losing your reasoning or tone.</p>
                </div>
              </div>

              <div className="call-detail-notes-grid">
                <aside className="call-detail-style-panel">
                  <div className="call-detail-panel-label">Styles</div>
                  {STYLES.map((style) => {
                    const hasNote = Boolean(call.notes?.[style.key]);
                    return (
                      <button
                        key={style.key}
                        data-testid={`style-${style.key}`}
                        onClick={() => setActiveStyle(style.key)}
                        className={`call-detail-style-button${activeStyle === style.key ? " is-active" : ""}`}
                      >
                        <span>{style.label}</span>
                        {hasNote && <span className="call-detail-style-button__ready" />}
                      </button>
                    );
                  })}
                </aside>

                <section className="call-detail-note-editor">
                  <div className="call-detail-toolbar">
                    <label className="call-detail-language">
                      <span>CRM note language</span>
                      <select value={crmNoteLanguage} onChange={(event) => setCrmNoteLanguage(event.target.value)} data-testid="crm-note-language">
                        <option value="english">English (default)</option>
                        <option value="conversation">Conversation language</option>
                      </select>
                    </label>

                    <button data-testid="generate-note-btn" onClick={() => generate(activeStyle)} disabled={generating} className="call-detail-primary-button call-detail-primary-button--compact">
                      <Sparkle size={14} weight="fill" />
                      {generating ? "Writing..." : currentNote ? "Regenerate" : "Generate"}
                    </button>

                    {currentNote && (
                      <>
                        <RewriteBtn onClick={() => rewrite("shorter")} label="Shorter" disabled={generating} />
                        <RewriteBtn onClick={() => rewrite("more_professional")} label="More professional" disabled={generating} />
                        <RewriteBtn onClick={() => rewrite("more_conversational")} label="More conversational" disabled={generating} />
                        <RewriteBtn onClick={() => rewrite("more_detailed")} label="More detailed" disabled={generating} />
                        <div className="call-detail-toolbar__exports">
                          <button onClick={copy} data-testid="copy-btn" className="call-detail-secondary-button"><Copy size={14} /> Copy</button>
                          <button onClick={() => download("md")} data-testid="download-md-btn" className="call-detail-secondary-button">.md</button>
                          <button onClick={() => download("txt")} data-testid="download-txt-btn" className="call-detail-secondary-button">.txt</button>
                        </div>
                      </>
                    )}
                  </div>

                  <div
                    className={`note-enrichment-strip is-${noteEnrichmentState.tone}`}
                    data-testid="note-enrichment-status"
                  >
                    <span className="note-enrichment-strip__icon" aria-hidden="true">
                      {findingsReady
                        ? <CheckCircle size={15} weight="fill" />
                        : <Sparkle size={15} weight="fill" />}
                    </span>
                    <span className="note-enrichment-strip__copy">
                      <strong>{noteEnrichmentState.title}</strong>
                      <small>{noteEnrichmentState.message}</small>
                    </span>
                  </div>

                  {currentNote ? (
                    <>
                      <textarea
                        data-testid="note-textarea"
                        value={currentNote}
                        onChange={(event) => setCall((current) => ({
                          ...current,
                          notes: {
                            ...(current.notes || {}),
                            [activeStyle]: {
                              ...(current.notes?.[activeStyle] || {}),
                              content: event.target.value,
                              scores: null,
                              dna_alignment: null,
                            },
                          },
                        }))}
                        className="call-detail-note-textarea"
                      />
                      <QualityScorePanel scores={call.notes?.[activeStyle]?.scores} />
                    </>
                  ) : (
                    <div className="call-detail-empty-state">
                      <RevealLinesMark />
                      <strong>Ready when you are.</strong>
                      <p>Generate this format and Callsect will write it using your Writing DNA.</p>
                    </div>
                  )}
                </section>

                <div className="call-detail-intelligence-column">
                  <IntelligencePanel callId={call.id} intelligence={call.intelligence_panel} onLoaded={(data) => setCall((current) => ({ ...current, intelligence_panel: data }))} />
                </div>
              </div>
            </section>
          )}

          {activeTab === "transcript" && (
            <section className="call-detail-transcript">
              <div className="call-detail-transcript__header">
                <div>
                  <div className="brand-eyebrow">Conversation record</div>
                  <h2>Transcript</h2>
                </div>
                <span>{(call.segments || []).length ? `${call.segments.length} segments` : "Full text"}</span>
              </div>
              {call.filename && (
                <audio ref={audioRef} onTimeUpdate={(event) => setCurrentTime(event.target.currentTime)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} className="hidden" />
              )}
              <div className="call-detail-transcript__body intel-scrollbar">
                {(call.segments || []).length === 0 ? (
                  <div className="call-detail-transcript__plain">{call.transcript}</div>
                ) : (
                  call.segments.map((segment, index) => (
                    <div
                      key={index}
                      data-transcript-index={index}
                      className={`transcript-row${highlightedTranscriptIndex === index ? " is-highlighted" : ""}`}
                    >
                      <div className="transcript-time">{fmt(segment.start)}</div>
                      <div className="transcript-copy">
                        <span className={`transcript-speaker ${transcriptSpeakerTone(segment.speaker)}`.trim()}>{segment.speaker || "Speaker"}</span>
                        <span>{segment.text}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          )}

          {activeTab === "analysis" && (
            <AnalysisView
              analysis={analysis}
              onRun={runAnalysis}
              loading={analyzing}
              findingsStep={findingsStep}
              relationshipPath={relationshipPath}
              relationshipName={relationshipName}
              relationshipReturnState={relationshipReturnState}
              onKeyMomentNavigate={openTranscriptAt}
              deepDiveTarget={deepDiveTarget}
            />
          )}

          {activeTab === "coaching" && (
            <CoachingView coaching={coaching} onRun={runAnalysis} loading={analyzing} />
          )}
        </div>
      </main>
    </div>
  );
}

function RewriteBtn({ onClick, label, disabled = false }) {
  return (
    <button disabled={disabled} onClick={onClick} data-testid={`rewrite-${label.toLowerCase().replace(/\s+/g, "-")}`} className="call-detail-secondary-button">
      <ArrowClockwise size={12} /> {label}
    </button>
  );
}

const SIGNAL_DESCRIPTIONS = {
  buyingIntent: "How strongly the conversation suggests a willingness to buy, renew, or move forward.",
  trust: "How much confidence the customer shows in the rep, company, and proposed solution.",
  relationship: "The overall strength and stability of the customer relationship.",
  confidence: "How certain and decisive the customer appears about their position.",
  churnRisk: "The likelihood that the customer may downgrade, cancel, or not renew.",
  quality: "The overall commercial quality and effectiveness of the conversation.",
};

const SKILL_DESCRIPTIONS = {
  empathy: "How well the rep acknowledges the customer's perspective and emotions.",
  listening: "How effectively the rep understands and responds to what the customer actually says.",
  conversationControl: "How well the rep guides the discussion toward a useful outcome without dominating it.",
};

function MetricHelp({ label, description }) {
  if (!description) return null;

  return (
    <span
      className="metric-help"
      tabIndex={0}
      aria-label={`${label}: ${description}`}
    >
      <Info size={11} weight="bold" aria-hidden="true" />
      <span className="metric-help__tooltip" role="tooltip">{description}</span>
    </span>
  );
}

function scoreTone(value, invert = false) {
  const normalized = Math.max(0, Math.min(100, value || 0));
  const interpreted = invert ? 100 - normalized : normalized;

  if (interpreted >= 90) return "excellent";
  if (interpreted >= 75) return "good";
  if (interpreted >= 60) return "caution";
  if (interpreted >= 40) return "warning";
  return "danger";
}

function Bar({ label, value, description, invert = false }) {
  const normalized = Math.max(0, Math.min(100, value || 0));
  const tone = scoreTone(normalized, invert);

  return (
    <div className={`findings-score findings-score--${tone}`}>
      <div className="findings-score__head">
        <div className="findings-score__label">
          <span className="findings-score__label-text">{label}</span>
          <MetricHelp label={label} description={description} />
        </div>
        <strong>{normalized}</strong>
      </div>
      <div className="score-meter">
        <div
          className={`score-meter__fill score-meter__fill--${tone}`}
          style={{ width: `${normalized}%` }}
        />
      </div>
    </div>
  );
}

function List({ title, items, icon: Icon, color = "text-slate-300", testIdPrefix }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="findings-list">
      <div className="findings-list__title">{Icon && <Icon size={13} weight="fill" />} {title}</div>
      <ul>
        {items.map((item, index) => {
          const { text, confidence } = itemParts(item);
          return (
            <li key={index} className={color} data-testid={testIdPrefix ? `${testIdPrefix}-${index}` : undefined}>
              <span className="findings-list__bullet" />
              <span>{text}<ConfChip confidence={confidence} testId={testIdPrefix ? `${testIdPrefix}-${index}-conf` : undefined} /></span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function CompactSignal({ label, value, invert = false }) {
  const numeric = Math.max(0, Math.min(100, Number(value) || 0));
  const interpreted = invert ? 100 - numeric : numeric;
  const tone = interpreted >= 75 ? "good" : interpreted >= 50 ? "caution" : "risk";
  return (
    <div className={`findings-signal-pill findings-signal-pill--${tone}`}>
      <span>{label}</span>
      <strong>{numeric}</strong>
    </div>
  );
}

function FindingGroup({ title, items, icon: Icon, tone = "violet", emptyText }) {
  const visible = Array.isArray(items) ? items.filter((item) => itemParts(item).text) : [];
  if (!visible.length && !emptyText) return null;
  return (
    <div className={`findings-classified-group findings-classified-group--${tone}`}>
      <div className="findings-classified-group__title">
        {Icon && <Icon size={14} weight="fill" aria-hidden="true" />}
        {title}
      </div>
      {visible.length ? (
        <ul>
          {visible.map((item, index) => {
            const { text, confidence } = itemParts(item);
            return (
              <li key={index}>
                <span className="findings-list__bullet" />
                <span>{text}<ConfChip confidence={confidence} /></span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="findings-classified-group__empty">{emptyText}</p>
      )}
    </div>
  );
}

function ActionList({ actions }) {
  const visible = Array.isArray(actions) ? actions.filter((item) => itemParts(item).text) : [];
  if (!visible.length) return null;
  const ownerLabel = {
    customer: "Customer",
    sales_rep: "Rep",
    other: "Other",
    unknown: "Owner unclear",
  };
  return (
    <ul className="findings-action-list">
      {visible.map((action, index) => {
        const text = action?.text || action?.what || itemParts(action).text;
        const owner = action?.owner || action?.who || "unknown";
        const deadline = action?.deadline;
        return (
          <li key={index}>
            <span className={`findings-owner-chip findings-owner-chip--${owner}`}>
              {ownerLabel[owner] || ownerLabel.unknown}
            </span>
            <span className="findings-action-list__copy">
              <strong>{localizeSellerRole(text)}</strong>
              {deadline && String(deadline).toLowerCase() !== "not established" && (
                <small>{deadline}</small>
              )}
            </span>
            <ConfChip confidence={action?.confidence} />
          </li>
        );
      })}
    </ul>
  );
}

function parseTimestampSeconds(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const match = raw.match(/^(?:(\d{1,2}):)?(\d{1,2}):(\d{2})$/);
  if (!match) return null;

  const hours = Number(match[1] || 0);
  const minutes = Number(match[2] || 0);
  const seconds = Number(match[3] || 0);
  if (minutes > 59 || seconds > 59) return null;

  return (hours * 3600) + (minutes * 60) + seconds;
}

function KeyMomentList({ items, onTimestampClick }) {
  const visible = Array.isArray(items) ? items.slice(0, 6) : [];
  if (!visible.length) return null;

  return (
    <div className="findings-key-moments">
      {visible.map((item, index) => {
        const timestamp = item?.timestamp || item?.timestamp_estimate || `0${index + 1}`;
        const seconds = parseTimestampSeconds(timestamp);
        const canNavigate = Number.isFinite(seconds) && typeof onTimestampClick === "function";

        return (
          <article key={index}>
            {canNavigate ? (
              <button
                type="button"
                className="findings-key-moment__timestamp"
                onClick={() => onTimestampClick(seconds)}
                title="Open this moment in the transcript"
              >
                {timestamp}
              </button>
            ) : (
              <span className="findings-key-moment__timestamp is-static">{timestamp}</span>
            )}
            <div>
              <strong>{localizeSellerRole(item?.description || itemParts(item).text)}</strong>
              {item?.quote && item.quote !== "no direct quote" && <q>{item.quote}</q>}
            </div>
            <ConfChip confidence={item?.confidence} />
          </article>
        );
      })}
    </div>
  );
}

function EvidenceFindingCard({ item, onTimestampClick, forceExpanded = false }) {
  const [expanded, setExpanded] = useState(forceExpanded);
  const finding = itemParts(item?.finding || item?.conclusion || item);
  const why = Array.isArray(item?.why)
    ? item.why.filter((reason) => itemParts(reason).text).slice(0, 3)
    : [];
  const moments = Array.isArray(item?.supporting_moments)
    ? item.supporting_moments.filter((moment) => (
        moment?.timestamp || moment?.timestamp_estimate
      )).slice(0, 3)
    : [];

  useEffect(() => {
    if (forceExpanded) setExpanded(true);
  }, [forceExpanded]);

  if (!finding.text || !moments.length) return null;

  return (
    <article className={`findings-evidence-card${expanded ? " is-expanded" : " is-collapsed"}`}>
      <button
        type="button"
        className="findings-evidence-card__toggle"
        onClick={() => setExpanded((current) => !current)}
        aria-expanded={expanded}
      >
        <span className="findings-evidence-card__finding">
          <strong>{localizeSellerRole(finding.text)}</strong>
        </span>
        <span className="findings-evidence-card__controls">
          <ConfChip confidence={finding.confidence} />
          <CaretDown size={16} weight="bold" aria-hidden="true" />
        </span>
      </button>

      {expanded && (
        <div className="findings-evidence-card__details">
          {why.length > 0 && (
            <div className="findings-evidence-card__why">
              <small>Why this conclusion?</small>
              <ul>
                {why.map((reason, index) => {
                  const parts = itemParts(reason);
                  return (
                    <li key={index}>
                      <CheckCircle size={13} weight="fill" aria-hidden="true" />
                      <span>{parts.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="findings-evidence-card__moments">
            <small>Supporting moments</small>
            <KeyMomentList items={moments} onTimestampClick={onTimestampClick} />
          </div>
        </div>
      )}
    </article>
  );
}

function EvidenceSection({ id, title, icon: Icon, items, onTimestampClick, forceExpanded = false }) {
  const rawItems = Array.isArray(items) ? items : items ? [items] : [];
  const visibleItems = rawItems.filter((item) => {
    const finding = itemParts(item?.finding || item?.conclusion || item);
    return finding.text && Array.isArray(item?.supporting_moments) && item.supporting_moments.length;
  });
  if (!visibleItems.length) return null;

  return (
    <section id={`deep-dive-${id}`} className="findings-evidence-section">
      <div className="findings-evidence-section__header">
        <Icon size={17} weight="fill" aria-hidden="true" />
        <h3>{title}</h3>
      </div>
      <div className="findings-evidence-grid">
        {visibleItems.map((item, index) => (
          <EvidenceFindingCard
            key={`${id}-${index}`}
            item={item}
            onTimestampClick={onTimestampClick}
            forceExpanded={forceExpanded}
          />
        ))}
      </div>
    </section>
  );
}

function AnalysisView({
  analysis,
  onRun,
  loading,
  findingsStep,
  relationshipPath,
  relationshipName,
  relationshipReturnState,
  onKeyMomentNavigate,
  deepDiveTarget,
}) {
  if (!analysis) {
    return (
      <section className="findings-empty-state">
        <div className="findings-empty-state__visual"><RevealLinesMark /></div>
        {loading ? (
          <div className="findings-processing findings-processing--centered">
            {["Scanning the conversation", "Connecting evidence"].map((label, index) => {
              const step = index + 1;
              const complete = findingsStep > step;
              const active = findingsStep === step;
              return (
                <div key={label} className={`findings-processing__step${active ? " is-active" : ""}${complete ? " is-complete" : ""}`}>
                  <span className="findings-processing__number">{complete ? "✓" : step}</span>
                  <span>{label}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div>
            <div className="brand-eyebrow">Conversation dissection</div>
            <h2>Reveal what really happened.</h2>
            <p>Surface the commercial status, real friction, agreed actions and next routes forward.</p>
          </div>
        )}
        <button onClick={onRun} disabled={loading} className="call-detail-primary-button">
          <Target size={16} weight="fill" /> {loading ? "Generating findings..." : "Start dissection"}
        </button>
      </section>
    );
  }

  const status = analysis.commercial_status || {};
  const deep = analysis.deep_dive || {};
  const takeaway = itemParts(analysis.takeaway || analysis.summary);
  const stage = status.stage || analysis.negotiation_stage;
  const decisionMaker = status.decision_maker || status.decision_owner || analysis.decision_maker_influence;
  const keyMoments = deep.key_moments || analysis.key_moments || [];
  const evidenceSections = [
    { id: "stance", title: "Stance", icon: Handshake, items: deep.stance },
    { id: "tone", title: "Tone", icon: ChatCircle, items: deep.tone },
    { id: "blockers", title: "Blockers", icon: WarningCircle, items: deep.blockers },
    { id: "objections", title: "Objections", icon: Flag, items: deep.objections },
    { id: "risks", title: "Risks", icon: Warning, items: deep.risks },
    { id: "opportunities", title: "Opportunities", icon: Lightbulb, items: deep.opportunities },
  ];
  const hasEvidenceSections = evidenceSections.some(({ items }) => {
    const candidates = Array.isArray(items) ? items : items ? [items] : [];
    return candidates.some((item) => (
      itemParts(item?.finding || item?.conclusion || item).text
      && Array.isArray(item?.supporting_moments)
      && item.supporting_moments.length
    ));
  });

  return (
    <div className="findings-view findings-view--classic" data-testid="analysis-view">
      <div className="findings-classic-grid">
        <section className="findings-signal-profile" style={{ animationDelay: "0ms" }}>
          <div className="findings-card__eyebrow">
            <ChartBar size={15} weight="fill" />
            Signal profile
          </div>
          <div className="findings-signal-profile__body">
            <Bar label="Buying intent" value={analysis.buying_intent} description={SIGNAL_DESCRIPTIONS.buyingIntent} />
            <Bar label="Trust" value={analysis.trust_level} description={SIGNAL_DESCRIPTIONS.trust} />
            <Bar label="Relationship" value={analysis.relationship_strength} description={SIGNAL_DESCRIPTIONS.relationship} />
            <Bar label="Confidence" value={analysis.confidence_level} description={SIGNAL_DESCRIPTIONS.confidence} />
            <Bar label="Churn risk" value={analysis.risk_of_churn} description={SIGNAL_DESCRIPTIONS.churnRisk} invert />
            <Bar label="Quality" value={analysis.conversation_quality} description={SIGNAL_DESCRIPTIONS.quality} />
          </div>
        </section>

        <section className="findings-key-takeaway" style={{ animationDelay: "60ms" }}>
          <div className="findings-card__eyebrow">
            <MagnifyingGlass size={15} weight="fill" />
            Key takeaway
          </div>
          <h2 className="findings-key-takeaway__text">{localizeSellerRole(takeaway.text)}</h2>
          <div className="findings-takeaway-grid">
            <Kv label="Stage" val={stage} icon={TrendUp} />
            <Kv label="Decision maker" val={decisionMaker} icon={UserCircle} />
          </div>
        </section>
      </div>

      <section id="deep-dive" className="findings-deep-dive is-open">
        <div className="findings-deep-dive__header">
          <span>
            <Brain size={19} weight="fill" />
            Deep dive
            <small>Why Callsect reached each conclusion</small>
          </span>
        </div>

        <div className="findings-deep-dive__content findings-deep-dive__content--evidence">
          {hasEvidenceSections ? (
            evidenceSections.map((section) => (
              <EvidenceSection
                key={section.id}
                {...section}
                onTimestampClick={onKeyMomentNavigate}
                forceExpanded={deepDiveTarget === section.id}
              />
            ))
          ) : (
            <div className="findings-deep-dive__legacy">
              <KeyMomentList items={keyMoments} onTimestampClick={onKeyMomentNavigate} />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Kv({ label, val, color = "text-slate-200", icon: Icon }) {
  if (!val) return null;
  const { text, confidence } = itemParts(val);
  if (!text || text === "not evident") return null;
  return (
    <div className="findings-kv">
      <span className="findings-kv__label">
        {Icon && <Icon size={13} weight="fill" aria-hidden="true" />}
        {label}
      </span>
      <strong className={color}>{text}<ConfChip confidence={confidence} /></strong>
    </div>
  );
}

function CoachingView({ coaching, onRun, loading }) {
  if (!coaching) {
    return (
      <section className="call-detail-empty-state call-detail-empty-state--large">
        <RevealLinesMark />
        <strong>No coaching yet.</strong>
        <p>Run the analysis to turn the conversation into practical coaching moves.</p>
        <button onClick={onRun} disabled={loading} className="call-detail-primary-button">
          <ChartBar size={16} weight="fill" /> {loading ? "Analyzing..." : "Run analysis"}
        </button>
      </section>
    );
  }

  return (
    <div className="coaching-view" data-testid="coaching-view">
      <section className="coaching-card coaching-card--skills" style={{ animationDelay: "0ms" }}>
        <div className="findings-card__eyebrow"><ChartBar size={15} weight="fill" /> Skills</div>
        <div className="coaching-card__body">
          <Bar label="Empathy" value={coaching.empathy_score} description={SKILL_DESCRIPTIONS.empathy} />
          <Bar label="Listening" value={coaching.listening_score} description={SKILL_DESCRIPTIONS.listening} />
          <Bar label="Conversation control" value={coaching.control_of_conversation} description={SKILL_DESCRIPTIONS.conversationControl} />
        </div>
      </section>

      <section className="coaching-card" style={{ animationDelay: "70ms" }}>
        <div className="findings-card__eyebrow"><CheckCircle size={15} weight="fill" /> What worked</div>
        <div className="coaching-card__body"><List title="Strengths to retain" items={coaching.what_worked} icon={Barbell} color="text-emerald-300" /></div>
      </section>

      <section className="coaching-card coaching-card--moves" style={{ animationDelay: "140ms" }}>
        <div className="findings-card__eyebrow"><Megaphone size={15} weight="fill" /> Coaching moves</div>
        <div className="coaching-moves">
          {coaching.coaching_moves?.map((move, index) => (
            <article key={index} className="coaching-move">
              <div className="coaching-move__header"><strong>{localizeSellerRole(move.title)}</strong><ConfChip confidence={move.confidence} /></div>
              <div className="coaching-move__section"><span>Situation</span><blockquote>“{localizeSellerRole(move.situation)}”</blockquote></div>
              <div className="coaching-move__section"><span>Better move</span><p>{localizeSellerRole(move.better_move)}</p></div>
              <div className="coaching-move__section"><span>Example wording</span><blockquote className="is-example">“{localizeSellerRole(move.example_phrase)}”</blockquote></div>
              <div className="coaching-move__section"><span>Why it works</span><p>{localizeSellerRole(move.why_it_works)}</p></div>
            </article>
          ))}
        </div>
      </section>

      <section className="coaching-card coaching-card--wide" style={{ animationDelay: "210ms" }}>
        <div className="findings-card__eyebrow"><Target size={15} weight="fill" /> Next conversation</div>
        <div className="coaching-card__columns">
          <List title="Missed opportunities" items={coaching.missed_opportunities} icon={ArrowCircleUpRight} />
          <List title="Questions to ask" items={coaching.questions_to_ask} icon={ChatsCircle} />
          <List title="Negotiation suggestions" items={coaching.negotiation_suggestions} icon={Handshake} />
        </div>
      </section>
    </div>
  );
}
